import { TAriProxyApiRatePlanPriceType } from '~/api/services/ari-proxy-api/index.type'
import { TCouponApplicationType } from '~/api/services/coupon-platform/index.type'
import { IYaPaymentApiPaymentMethod } from '~/api/services/ya-payment-api/index.type'
import {
  TMemberClassBenefit,
  TMemberClassGlobalBadge,
} from '~/api/services/member-class-api/index.type'

type TGlobalOrderLanguageValue = {
  en?: string
  ko?: string
}
export const GlobalOrderListParamQuery = {
  // 체크인(예정)
  UPCOMING: 'UPCOMING',
  // 체크아웃 완료
  PAST: 'PAST',
  // 취소된 숙소
  CANCELED: 'CANCELED',
  // 전체
  ALL: 'ALL',
} as const
export type TGlobalOrderListParamQuery =
  (typeof GlobalOrderListParamQuery)[keyof typeof GlobalOrderListParamQuery]

export const GlobalOrderStatus = {
  RESERVED: 'RESERVED',
  PENDING: 'PENDING',
  CANCELED: 'CANCELED',
}
export type TGlobalOrderStatus = (typeof GlobalOrderStatus)[keyof typeof GlobalOrderStatus]

export const GlobalOrderPaymentType = {
  PG: 'PG',
  POINT: 'POINT',
  NOL_POINT: 'NOL_POINT',
  CJ_ONE_POINT: 'CJ_ONE_POINT',
}
export type TGlobalOrderPaymentType =
  (typeof GlobalOrderPaymentType)[keyof typeof GlobalOrderPaymentType]

export const GlobalOrderRefundType = {
  FULL: 'FULL',
  PARTIAL: 'PARTIAL',
  NONE: 'NONE',
}
export type TGlobalOrderRefundType =
  (typeof GlobalOrderRefundType)[keyof typeof GlobalOrderRefundType]

export const GlobalOrderCancelErrorCode = {
  // 이미 취소된 주문
  ORD_CANCEL_001: 'ORD_CANCEL_001',
  // 취소 가능 시점이 지난 주문
  ORD_CANCEL_002: 'ORD_CANCEL_002',
  // 취소 수수료 사전 검증 실패 (정책 조회 불가, 계산 금액 불일치)
  ORD_CANCEL_003: 'ORD_CANCEL_003',
  // 기타 취소 실패
  ORD_CANCEL_004: 'ORD_CANCEL_004',
  // 멀티룸 예약 취소 실패
  ORD_CANCEL_005: 'ORD_CANCEL_005',
} as const
export type TGlobalOrderCancelErrorCode =
  (typeof GlobalOrderCancelErrorCode)[keyof typeof GlobalOrderCancelErrorCode]

export const GlobalOrderCancelCause = {
  // 사용자 취소
  USER_REQUEST: 'USER_REQUEST',
  // 대기시간 초과로 인한 취소
  TIMEOUT: 'TIMEOUT',
  // 확정 실패
  TIME_OUT_AFTER_HOLDING: 'TIME_OUT_AFTER_HOLDING',
  // 확정 실패에 따른 취소
  CONFIRMATION_FAIL: 'CONFIRMATION_FAIL',
  // Rate 없음
  NO_RATE_AFTER_HOLDING: 'NO_RATE_AFTER_HOLDING',
  // 가격 불일치
  PRICE_MISMATCH_AFTER_HOLDING: 'PRICE_MISMATCH_AFTER_HOLDING',
  // 기타 실패
  FAILED_AFTER_HOLDING: 'FAILED_AFTER_HOLDING',
  // 관리자 일반 취소 요청
  ADMIN_REQUEST: 'ADMIN_REQUEST',
  // 관리자 미환불 취소 (환불금액 없음)
  ADMIN_CANCEL_NO_REFUND: 'ADMIN_CANCEL_NO_REFUND',
  // 관리자 전액 환불 취소
  ADMIN_CANCEL_FREE: 'ADMIN_CANCEL_FREE',
}
export type TGlobalOrderCancelCause =
  (typeof GlobalOrderCancelCause)[keyof typeof GlobalOrderCancelCause]

export const GlobalOrderCancelPolicyCriteria = {
  NIGHTS: 'NIGHTS',
  RATIO: 'RATIO',
  FIXED: 'FIXED',
  FREE: 'FREE',
  TOTAL: 'TOTAL',
  NON_REFUNDABLE: 'NON_REFUNDABLE',
}
export type TGlobalOrderCancelPolicyCriteria =
  (typeof GlobalOrderCancelPolicyCriteria)[keyof typeof GlobalOrderCancelPolicyCriteria]

export const GlobalOrderCancellationStatus = {
  NONE: 'NONE',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
}
export type TGlobalOrderCancellationStatus =
  (typeof GlobalOrderCancellationStatus)[keyof typeof GlobalOrderCancellationStatus]

export const GlobalOrderWarrantyPolicyResultCode = {
  // 보장 철회 가능
  CANCEL_AVAILABLE: 'CANCEL_AVAILABLE',
  // 취소 전 보장상품 사용 가능
  PRE_CANCEL_CLAIM_AVAILABLE: 'PRE_CANCEL_CLAIM_AVAILABLE',
  // 취소 전 보장상품 사용 불가 (체크인 24시간 이내)
  PRE_CANCEL_CLAIM_UNAVAILABLE_CLOSE_TO_CHECKIN: 'PRE_CANCEL_CLAIM_UNAVAILABLE_CLOSE_TO_CHECKIN',
  // 취소 전 보장상품 사용 불가 (취소 수수료 무료)
  PRE_CANCEL_CLAIM_UNAVAILABLE_CANCELLATION_FEE_ZERO:
    'PRE_CANCEL_CLAIM_UNAVAILABLE_CANCELLATION_FEE_ZERO',
  // 보장 철회됨
  CANCELED: 'CANCELED',
  // 보장상품 사용 가능
  CLAIM_AVAILABLE: 'CLAIM_AVAILABLE',
  // 보장상품 사용 불가 (체크인 24시간 이내)
  CLAIM_UNAVAILABLE_CLOSE_TO_CHECKIN: 'CLAIM_UNAVAILABLE_CLOSE_TO_CHECKIN',
  // 보장상품 사용 불가 (취소 수수료 무료)
  CLAIM_UNAVAILABLE_CANCELLATION_FEE_ZERO: 'CLAIM_UNAVAILABLE_CANCELLATION_FEE_ZERO',
  // 환불 심사 중
  CLAIM_UNDER_REVIEW: 'CLAIM_UNDER_REVIEW',
  // 환불 심사 완료(심사 거절)
  CLAIM_REVIEW_FINISHED: 'CLAIM_REVIEW_FINISHED',
  // 환불 완료
  CLAIM_REFUNDED: 'CLAIM_REFUNDED',
  // 가입 실패
  BOOK_FAILED: 'BOOK_FAILED',
} as const
export type TGlobalOrderWarrantyPolicyResultCode =
  (typeof GlobalOrderWarrantyPolicyResultCode)[keyof typeof GlobalOrderWarrantyPolicyResultCode]

export interface ICommonGlobalOrder {
  id: string
  gwOid: string // controller 에서 추가되는 값 (for kinesis)
  createdAt: string
  roomTypeId: string
  roomTypeVersion: number
  roomTypeName: string
  propertyName: string
  propertyId: string
  propertyVersion: number
  checkIn: string
  checkInEnd: string
  checkOut: string
  status: TGlobalOrderStatus
  price: number
  payments: {
    type: TGlobalOrderPaymentType
    method: string
    amount: number
    completedAt: string
  }[]
  discount?: {
    coupons: {
      amount: number
      id: number
      applicationType?: TCouponApplicationType | null
    }[]
  }
  isCancellable: boolean
  refund?: {
    type: TGlobalOrderRefundType
    endAt: string
  }
  priceType?: TAriProxyApiRatePlanPriceType
  /**
   * 캔슬프리 가입 한 경우에만 값 존재 (미가입시 null)
   * */
  warranty?: {
    warrantyId: number
    warrantyStatusCode: string
    price: number
    // 보장 상품 청구 URL
    claimUrl: string
    payments: {
      type: TGlobalOrderPaymentType
      method: string
      amount: number
      paymentMethod: IYaPaymentApiPaymentMethod
    }[]
    discount?: {
      coupons: {
        amount: number
        id: number
      }[]
    }
    cancelPolicies: {
      startAt: string
      endAt: string
      // 보장상품은 0% 또는 100% 만 있음
      ratio: number
    }[]
    // 보장상품 정책 코드
    warrantyPolicyResultCode: TGlobalOrderWarrantyPolicyResultCode
    // 보장상품 적용 시작 시점
    warrantyStartAt?: string
    // 보장상품 적용 종료 시점 (체크인 24시간 전)
    warrantyExpireAt: string
  } | null
  /** 주요 안내 정보 */
  notices?: {
    ko?: string
    en?: string
  }
  /** 리워드 정보 */
  rewardAmounts: {
    type: string
    eventType?: string
    amount?: number
  }[]
  /** 공급사명 */
  supplierName: string
  /** 취소 규정 (취소할 수 있는 경우 만) */
  cancelPolicies?: {
    startAt: string
    endAt: string
    criteria: TGlobalOrderCancelPolicyCriteria
    night?: number
    ratio?: number
    amount?: number
  }[]
}

export interface IGlobalOrderApiListParams {
  page: number
  size: number
  startDate?: string
  // 질의 유형
  query: TGlobalOrderListParamQuery
}
export interface IGlobalOrderApiList {
  page: number
  size: number
  total: number
  orders: ICommonGlobalOrder[]
}

export interface IGlobalOrderApiDetail extends ICommonGlobalOrder {
  cancelCause: TGlobalOrderCancelCause
  rooms: {
    adults: number
    childAges?: number[]
  }[]
  rateAmenities?: {
    code: string
    value: TGlobalOrderLanguageValue
  }[]
  surcharges?: {
    name: string
    charge: string
    inclusive: number
    currency: string | null
  }[]
  bedGroups?: {
    type: {
      code: string
      value: TGlobalOrderLanguageValue
    }
    quantity: number
  }[]
  preferredOption?: {
    bedGroup: {
      id: string
      value: string
    }
  }
  bedSummary?: string
  maximumGuests: number
  houseManual?: TGlobalOrderLanguageValue
  checkinInstructions?: TGlobalOrderLanguageValue
  checkinSpecialInstructions?: TGlobalOrderLanguageValue
  ratePlanDescriptions?: TGlobalOrderLanguageValue
  customerName: string
  customerPhone: string
  customerEmail: string
  checkInUsers: {
    lastName: string
    firstName: string
  }[]
  cancelPolicies: {
    startAt: string
    endAt: string
    criteria: TGlobalOrderCancelPolicyCriteria
    night?: number
    ratio?: number
    amount?: number
  }[]
  ratePlanId: string
  specialRequest?: string
  optionalSpecialRequest?: {
    ko: string
    en: string
  }[]
  cancellation?: {
    cancellationStatus: TGlobalOrderCancellationStatus
    refundStatus: TGlobalOrderCancellationStatus
    payments: {
      type: TGlobalOrderPaymentType
      method: string
      amount: number
      commissionAmount: number
      requestedAt?: string
      completedAt?: string
    }[]
    discount?: {
      coupons: {
        id: number
        amount: number
        applicationType?: TCouponApplicationType | null
        commissionAmount: number
        requestedAt: string
        completedAt?: string
      }[]
    }
  }
  cancelRequestedAt?: string
  cancelCompletedAt?: string
  priceType?: TAriProxyApiRatePlanPriceType
  salePriceDiscountAmount?: number
  warranty?:
    | (NonNullable<ICommonGlobalOrder['warranty']> & {
        // 환불 여부
        refund: {
          type: TGlobalOrderRefundType
          endAt: string
        }
        cancellation: {
          // COMPLETED 만 가능
          cancellationStatus: 'COMPLETED'
          // COMPLETED 만 가능
          refundStatus: 'COMPLETED'
          payments: Array<
            NonNullable<ICommonGlobalOrder['warranty']>['payments'][number] & {
              commissionAmount: number
            }
          >
        }
      })
    | null
  displayBadge: TMemberClassGlobalBadge
  benefitCode: TMemberClassBenefit
  totalRewardAmount?: number
}

export interface IGlobalOrderApiCancellationPreview
  extends Omit<IGlobalOrderApiDetail, 'payments' | 'warranty'> {
  payments: {
    type: TGlobalOrderPaymentType
    method: string
    amount: number
    commissionAmount: number
  }[]
  discount?: {
    coupons: {
      amount: number
      id: number
      applicationType?: TCouponApplicationType | null
      commissionAmount: number
    }[]
  }
  pointRewardPolicyCode: string
  warranty?:
    | (Omit<NonNullable<ICommonGlobalOrder['warranty']>, 'payments' | 'discount'> & {
        payments: Array<
          NonNullable<ICommonGlobalOrder['warranty']>['payments'][number] & {
            commissionAmount: number
          }
        >
        discount?: Required<NonNullable<ICommonGlobalOrder['warranty']>['discount']> & {
          coupons: {
            id: number
            amount: number
            commissionAmount: number
          }[]
        }
      })
    | null
}

export interface IGlobalOrderWarrantyFreeAvailabilityParams {
  propertyId: string
  roomTypeId: string
  checkInDate: string
  noneAuthenticated?: boolean
}

export interface IGlobalOrderWarrantyFreeAvailability {
  isFreeAvailable: boolean
}

export interface IGlobalOrderWarrantyQuoteParams {
  propertyId: string
  roomTypeId: string
  checkInDate: string
  checkOutDate: string
  rooms: {
    adults: number
    childAges?: number[]
  }[]
  ratePlanId: string
  provisionCode: string
  appType?: string
}

interface IGlobalOrderWarrantyQuoteAvailable {
  isAvailable: true
  warrantyId: number
  price: number
  coupon?: {
    couponId: number
  }
}
interface IGlobalOrderWarrantyQuoteUnavailable {
  isAvailable: false
}

export type IGlobalOrderWarrantyQuote =
  | IGlobalOrderWarrantyQuoteAvailable
  | IGlobalOrderWarrantyQuoteUnavailable

export interface GlobalOrderFinalCheckParams {
  propertyId: string
  roomTypeId: string
  checkInDate: string
  checkOutDate: string
  rooms: {
    adults: number
    childAges?: number[]
  }[]
  salePrice: number
  ratePlanId: string
  provisionCode: string
  supplier: string
}

export interface GlobalOrderFinalCheckResponse {
  notice: string | null
}

export interface IGlobalOrderApiCancelOrderFDSMeta {
  channelCode: string
  deviceId: string
  deviceModel: string
  devicePlatform: string
  appVersion: string
  remoteAddress: string
  userAgent: string
  mobileNetworkCode?: string
  mobileCountryCode?: string
  deviceTimezone: string
  isVpn?: boolean
  isRoaming?: boolean
}
